import { createClient, type Session } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

/**
 * URL + anon key của project, dùng cho các trang công khai (không đăng nhập).
 * Anon key là khóa công khai, được nhúng vào bundle là bình thường.
 */
export const SUPABASE_URL = supabaseUrl;
export const SUPABASE_ANON_KEY = supabaseAnonKey;

/**
 * Khóa tùy chỉnh cho Navigator LockManager, có hạn mức chờ tối đa 10 giây.
 *
 * Vấn đề: khi mở nhiều tab cùng lúc, Supabase dùng LockManager để tuần tự hóa
 * các thao tác ghi phiên (refresh / signOut). Nếu một tab bị treo hoặc đóng đột
 * ngột khi đang giữ khóa, các tab khác sẽ chờ khóa không hồi kết -> trang bị
 * "đứng hình". Hàm này chặn chờ quá 10 giây để tránh tình trạng đó.
 */
async function acquireLock<T>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<T>
): Promise<T> {
  // Trình duyệt không hỗ trợ LockManager -> chạy thẳng, không khóa
  if (!navigator.locks?.request) {
    return fn();
  }

  const timeoutMs = acquireTimeout > 0 ? acquireTimeout : 10000;
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => abortController.abort(), timeoutMs);

  try {
    return await navigator.locks.request(
      name,
      { mode: "exclusive", signal: abortController.signal },
      fn
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    /**
     * TẮT tự gia hạn ngầm của Supabase.
     *
     * Lý do: khi refresh token trong trình duyệt đã bị thu hồi / xoá phía
     * server (đăng nhập mới ở máy khác, session bị admin xoá, token hết hạn
     * quá lâu...), bộ hẹn giờ ngầm của Supabase sẽ gọi refresh và ném lỗi
     * "Invalid Refresh Token: Refresh Token Not Found" ra ngoài dưới dạng
     * promise không ai bắt -> lỗi đỏ hiện giữa màn hình.
     *
     * Thay vào đó, việc gia hạn được chủ động làm trong AuthContext
     * (hàm ensureSessionFresh) nên mọi lỗi đều được bắt và dọn sạch phiên.
     */
    autoRefreshToken: false,
    detectSessionInUrl: true,
    lock: acquireLock,
  },
});

/** Tiền tố khóa lưu phiên của supabase-js: sb-<project-ref>-auth-token */
function isAuthStorageKey(key: string): boolean {
  return key.startsWith("sb-") && key.includes("-auth-token");
}

/**
 * Xoá phiên đăng nhập đang lưu trong trình duyệt.
 * Dùng khi token đã hỏng, để lần tải sau không còn cố gia hạn nó nữa.
 */
export function clearStoredAuthSession(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && isAuthStorageKey(key)) keys.push(key);
    }
    keys.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Trình duyệt chặn localStorage -> bỏ qua, không làm hỏng trang
  }

  try {
    const keys: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key && isAuthStorageKey(key)) keys.push(key);
    }
    keys.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // Bỏ qua
  }
}

/** Dọn phiên: đăng xuất cục bộ (không gọi server) rồi xoá token đã lưu. */
export async function abandonSession(): Promise<void> {
  try {
    await supabase.auth.signOut({ scope: "local" });
  } catch {
    // Token đã hỏng thì signOut cũng có thể lỗi -> vẫn xoá tay bên dưới
  }
  clearStoredAuthSession();
}

export type SessionCheck =
  | { status: "ok"; session: Session }
  | { status: "none" }
  | { status: "failed" };

/** Gia hạn còn dư bao nhiêu giây thì chủ động làm mới trước. */
const REFRESH_MARGIN_SECONDS = 120;

/**
 * Bảo đảm phiên đăng nhập còn dùng được:
 * - Chưa đăng nhập -> none
 * - Token còn hạn -> ok
 * - Token sắp/đã hết hạn -> tự gia hạn; nếu server từ chối thì dọn phiên -> failed
 *
 * Mọi lỗi đều được bắt tại đây nên không còn lỗi "Invalid Refresh Token"
 * văng ra ngoài màn hình.
 */
export async function ensureSessionFresh(): Promise<SessionCheck> {
  let current: Session | null = null;

  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      await abandonSession();
      return { status: "failed" };
    }
    current = data.session;
  } catch {
    await abandonSession();
    return { status: "failed" };
  }

  if (!current) return { status: "none" };

  const nowSeconds = Math.floor(Date.now() / 1000);
  const expiresAt = current.expires_at ?? 0;
  if (expiresAt - nowSeconds > REFRESH_MARGIN_SECONDS) {
    return { status: "ok", session: current };
  }

  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session) {
      await abandonSession();
      return { status: "failed" };
    }
    return { status: "ok", session: data.session };
  } catch {
    await abandonSession();
    return { status: "failed" };
  }
}

/**
 * Lấy access token còn hạn để gọi Edge Function.
 * Tự gia hạn khi cần, trả null nếu phiên đã chết.
 */
export async function getFreshAccessToken(): Promise<string | null> {
  const result = await ensureSessionFresh();
  return result.status === "ok" ? result.session.access_token : null;
}